import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { openExternal } from '../../../src/utils/openExternal';
import { useTheme } from '../../../src/context/ThemeContext';
import ContentContainer from '../../../src/components/ContentContainer';
import PageFooter from '../../../src/components/PageFooter';
import { api } from '../../../src/api/client';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';

interface CmsDiagram {
  title: string;
  url: string;
  source: string;
}

interface CmsSection {
  icon?: keyof typeof Ionicons.glyphMap;
  title: string;
  anchor?: string;
  content: string[];
  diagrams?: CmsDiagram[];
  image?: string;
  /** 1.4 P33 */
  callouts?: { kind?: 'info' | 'warning' | 'danger'; title?: string; text: string }[];
  terminalTable?: { title?: string; columns: string[]; rows: string[][] };
  comparison?: { columns: string[]; rows: { label: string; values: string[] }[] };
}

interface CmsPage {
  slug: string;
  title: string;
  version: string | null;
  lastUpdated: string | null;
  sections: CmsSection[];
}

/**
 * Renders a paragraph string with lightweight markdown:
 *   - **bold**
 *   - [text](url) — either an http(s) URL or `#anchor` for in-page navigation
 *   - \n — preserved as line breaks inline
 *
 * Uses <Text onPress> for links (not TouchableOpacity) so inline wrapping
 * survives mid-paragraph. No nested formatting; three primitives only.
 */
function renderParagraph(
  text: string,
  styles: ReturnType<typeof createStyles>,
  onAnchorPress: (anchor: string) => void,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const tokenRe = /(\*\*[^*]+\*\*|\[[^\]]+\]\((?:#[a-z0-9-]+|https?:\/\/[^\s)]+|mailto:[^\s)]+)\))/g;
  let lastIdx = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<Text key={`t-${key++}`}>{text.slice(lastIdx, match.index)}</Text>);
    }
    const tok = match[0];
    if (tok.startsWith('**')) {
      parts.push(
        <Text key={`b-${key++}`} style={styles.bold}>{tok.slice(2, -2)}</Text>,
      );
    } else {
      const linkMatch = /^\[([^\]]+)\]\((.+)\)$/.exec(tok);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        const isAnchor = href.startsWith('#');
        parts.push(
          <Text
            key={`l-${key++}`}
            style={styles.link}
            onPress={() => {
              if (isAnchor) onAnchorPress(href.slice(1));
              else openExternal(href);
            }}
          >
            {label}
          </Text>,
        );
      } else {
        parts.push(<Text key={`u-${key++}`}>{tok}</Text>);
      }
    }
    lastIdx = tokenRe.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(<Text key={`t-${key++}`}>{text.slice(lastIdx)}</Text>);
  }
  return parts;
}

export default function InstallationGuide() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<ScrollView>(null);
  const sectionOffsets = useRef<Record<string, number>>({});

  useEffect(() => {
    const ac = new AbortController();
    api<CmsPage>('/api/cms/pages/installation-guide', { skipAuth: true })
      .then((d) => { if (!ac.signal.aborted) setPage(d); })
      .catch((err) => { if (!ac.signal.aborted) console.error('fetch install guide:', err); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, []);

  const scrollToAnchor = useCallback((anchor: string) => {
    const y = sectionOffsets.current[anchor];
    if (y !== undefined) {
      scrollRef.current?.scrollTo({ y: Math.max(0, y - spacing.md), animated: true });
    }
  }, []);

  const renderParagraphMemo = useMemo(
    () => (text: string) => renderParagraph(text, styles, scrollToAnchor),
    [styles, scrollToAnchor],
  );

  if (loading) {
    return (
      <ContentContainer role="owner" footer={false}>
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </ContentContainer>
    );
  }

  return (
    // footer=false: this container wraps the scroll view, so its footer would
    // pin to the viewport bottom — PageFooter renders at the scroll end instead.
    <ContentContainer role="owner" footer={false}>
      <ScrollView
        ref={scrollRef}
        style={styles.container}
        contentContainerStyle={{ paddingBottom: spacing.xl }}
      >
        <Text style={styles.pageTitle}>{page?.title || 'Installation Guide'}</Text>
        <Text style={styles.pageSubtitle}>
          Brand-specific wiring instructions for every wash type.
        </Text>

        {page?.sections.map((section, i) => (
          <View
            key={section.anchor ?? i}
            style={styles.card}
            onLayout={(e) => {
              if (section.anchor) {
                sectionOffsets.current[section.anchor] = e.nativeEvent.layout.y;
              }
            }}
          >
            <View style={styles.cardHeader}>
              {section.icon && <Ionicons name={section.icon} size={24} color={colors.primary} />}
              <Text style={styles.cardTitle}>{section.title}</Text>
            </View>
            {section.image && (
              <Image source={{ uri: section.image }} style={styles.image} resizeMode="contain" />
            )}
            {/* 1.4 P33: controller comparison + terminal tables + callouts */}
            {section.comparison && (
              <View style={styles.table}>
                <View style={[styles.tr, styles.th]}><Text style={[styles.td, styles.tdLabel, styles.thText]} /> {section.comparison.columns.map((c, k) => <Text key={k} style={[styles.td, styles.thText]}>{c}</Text>)}</View>
                {section.comparison.rows.map((r, k) => (
                  <View key={k} style={styles.tr}><Text style={[styles.td, styles.tdLabel]}>{r.label}</Text>{r.values.map((v, m) => <Text key={m} style={styles.td}>{renderParagraph(v, styles, scrollToAnchor)}</Text>)}</View>
                ))}
              </View>
            )}
            {section.terminalTable && (
              <View style={styles.table}>
                {!!section.terminalTable.title && <Text style={styles.tableTitle}>{section.terminalTable.title}</Text>}
                <View style={[styles.tr, styles.th]}>{section.terminalTable.columns.map((c, k) => <Text key={k} style={[styles.td, styles.thText]}>{c}</Text>)}</View>
                {section.terminalTable.rows.map((r, k) => (
                  <View key={k} style={styles.tr}>{r.map((v, m) => <Text key={m} style={styles.td}>{renderParagraph(v, styles, scrollToAnchor)}</Text>)}</View>
                ))}
              </View>
            )}
            {section.callouts?.map((c, k) => (
              <View key={k} style={[styles.callout, c.kind === 'warning' && styles.calloutWarning, c.kind === 'danger' && styles.calloutDanger]}>
                {!!c.title && <Text style={styles.calloutTitle}>{c.title}</Text>}
                <Text style={styles.cardText}>{renderParagraph(c.text, styles, scrollToAnchor)}</Text>
              </View>
            ))}
            {section.content.map((paragraph, j) => (
              <Text key={j} style={styles.cardText}>
                {renderParagraphMemo(paragraph)}
              </Text>
            ))}
            {section.diagrams && section.diagrams.length > 0 && (
              <View style={styles.diagramsWrap}>
                <Text style={styles.diagramsHeading}>Wiring diagrams</Text>
                {section.diagrams.map((d, k) => (
                  <Text
                    key={k}
                    style={styles.diagramLink}
                    onPress={() => openExternal(d.url)}
                  >
                    📄 {d.title}
                    <Text style={styles.diagramSource}>   — {d.source}</Text>
                  </Text>
                ))}
              </View>
            )}
          </View>
        ))}
        <PageFooter />
      </ScrollView>
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  table: { marginBottom: spacing.md, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, overflow: 'hidden' },
  tableTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, padding: spacing.sm, backgroundColor: c.surfaceSecondary },
  tr: { flexDirection: 'row', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight },
  th: { backgroundColor: c.surfaceSecondary },
  thText: { fontWeight: fontWeight.semibold, color: c.primary },
  td: { flex: 1, fontSize: fontSize.xs, color: c.text, padding: 6 },
  tdLabel: { fontWeight: fontWeight.semibold, flex: 1.2 },
  callout: { borderLeftWidth: 4, borderLeftColor: c.primary, backgroundColor: c.surface, padding: spacing.sm, borderRadius: borderRadius.sm, marginBottom: spacing.sm },
  calloutWarning: { borderLeftColor: c.warning },
  calloutDanger: { borderLeftColor: c.danger },
  calloutTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: 2 },
  container: { flex: 1, padding: spacing.md },
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  pageSubtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.lg },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  cardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, flex: 1 },
  cardText: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 22, marginBottom: spacing.sm },
  bold: { fontWeight: fontWeight.semibold, color: c.text },
  link: { color: c.primary, textDecorationLine: 'underline' },
  image: { width: '100%', height: 220, marginBottom: spacing.sm, borderRadius: borderRadius.sm },
  diagramsWrap: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  diagramsHeading: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  diagramLink: { fontSize: fontSize.sm, color: c.primary, lineHeight: 22, marginBottom: spacing.xs },
  diagramSource: { color: c.textSecondary, fontWeight: fontWeight.normal },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
